<template>
    <div class="c-mqs col-xl-8 col-lg-8 col-md-8">
        <!-- Mobile-only sub-step trail. Shows Service → Invoice → Payment →
             Amounts as a pill row above the questions so the user gets a
             sense of progress through what's otherwise a long stacked form. -->
        <SubStepTrail
            v-if="isMobile"
            :sub-idx="subStepIndex"
            :answers="subStepAnswers"
        />

        <!-- ===== Q1: Did you provide service? ===== -->
        <!-- On mobile, once Q4 is active we hide Q1/Q2/Q3 cards entirely so
             the Amounts screen reads as a focused step instead of a long
             scroll past three answered questions. Desktop keeps the stack. -->
        <CCard v-if="!isMobile || subStepIndex !== 3" :extra-style="cardStyle">
            <div :style="qHeadStyle">
                <span :style="qNumStyle">1</span>
                <span :style="qLabelStyle">Question 1 of 3</span>
            </div>
            <h2 :style="qTitleStyle">
                Did you provide service(-s) in
                <span class="red" :style="accentStyle">{{ currentMonth }}</span>?
            </h2>
            <div :style="choicePairStyle">
                <CChoice
                    icon="✓"
                    label="Yes, we did"
                    sub="I'll continue and log the details"
                    tone="green"
                    :selected="questionnnare.is_provide_service === true"
                    @click="onChangeProvideService(true)"
                />
                <CChoice
                    icon="–"
                    label="No, we didn't"
                    sub="I'll explain why instead"
                    tone="neutral"
                    :selected="questionnnare.is_provide_service === false"
                    @click="onChangeProvideService(false)"
                />
            </div>

            <!-- Inline No-service reason (preserved from original) -->
            <div v-if="questionnnare.is_provide_service === false" :style="reasonBlockStyle">
                <p :style="reasonTitleStyle">
                    Please provide reason why no services were provided in
                    <span class="red" :style="accentStyle">{{ currentMonth }}</span>
                </p>
                <div :style="reasonGridStyle">
                    <label
                        v-for="(reason, index) in saleLineItem.deal_line_item.type_id !== 1 ? reasonsList.slice(0, 5) : reasonsList"
                        :key="`${reason.id}__${saleLineItem.period}__${index}`"
                        :for="`reason_${reason.id}`"
                        :style="reasonOptionStyle(selectedReason === reason.id)"
                    >
                        <input
                            type="radio"
                            :checked="selectedReason === reason.id"
                            :id="`reason_${reason.id}`"
                            name="reason_id"
                            :value="reason.id"
                            :style="srOnly"
                            @change="$emit('update:selected-reason', Number($event.target.value)); $emit('change-reason', $event)"
                        />
                        <span :style="reasonRadioDotStyle(selectedReason === reason.id)">
                            <span v-if="selectedReason === reason.id" :style="reasonRadioInnerStyle" />
                        </span>
                        <span :style="reasonOptionLabelStyle">{{ reason.name }}</span>
                    </label>
                </div>
                <div
                    :key="!saleLineItem.is_new_project && !saleLineItem.is_new_retainer
                        ? `${saleLineItem.deal_line_item.id}__${saleLineItem.period}__explanation`
                        : `${saleLineItem.id}__explanation`"
                    :style="explanationFieldStyle"
                >
                    <label :style="labelStyle">Explanation</label>
                    <textarea
                        v-model="saleLineItem.explanation"
                        :style="textareaStyle"
                        @input="onExplanationInput"
                    ></textarea>
                </div>
            </div>
        </CCard>

        <!-- ===== Q2: Did you issue invoice? ===== -->
        <CCard
            v-if="questionnnare.is_provide_service && (!isMobile || subStepIndex !== 3)"
            :extra-style="cardStyle"
        >
            <div :style="qHeadStyle">
                <span :style="qNumStyle">2</span>
                <span :style="qLabelStyle">Question 2 of 3</span>
            </div>
            <h2 :style="qTitleStyle">
                Did you issue invoice in
                <span class="red" :style="accentStyle">{{ currentMonth }}</span>?
            </h2>
            <div :style="choicePairStyle">
                <CChoice
                    icon="✓"
                    label="Yes, invoiced"
                    sub="I have the invoice number"
                    tone="green"
                    :selected="questionnnare.is_issue_invoice === true"
                    @click="onChangeIssueService(true)"
                />
                <CChoice
                    icon="–"
                    label="Not yet"
                    sub="Billing delayed or held"
                    tone="neutral"
                    :selected="questionnnare.is_issue_invoice === false"
                    @click="onChangeIssueService(false)"
                />
            </div>

            <!-- Inline No-invoice (billing-delayed) reason picker -->
            <div v-if="questionnnare.is_issue_invoice === false" :style="reasonBlockStyle">
                <p :style="reasonTitleStyle">
                    Please provide reason why billing was delayed in
                    <span class="red" :style="accentStyle">{{ currentMonth }}</span>
                </p>
                <div :style="reasonGridStyle">
                    <label
                        v-for="(reason, index) in saleLineItem.deal_line_item.type_id === 2 ? billingReasons : billingReasons.slice(1, billingReasons.length)"
                        :key="`${reason.id}__${saleLineItem.period}_${index}`"
                        :for="`reason_${reason.id}`"
                        :style="reasonOptionStyle(selectedReason === reason.id)"
                    >
                        <input
                            type="radio"
                            :checked="selectedReason === reason.id"
                            :id="`reason_${reason.id}`"
                            name="reason_id"
                            :value="reason.id"
                            :style="srOnly"
                            @change="$emit('update:selected-reason', Number($event.target.value)); $emit('change-reason', $event)"
                        />
                        <span :style="reasonRadioDotStyle(selectedReason === reason.id)">
                            <span v-if="selectedReason === reason.id" :style="reasonRadioInnerStyle" />
                        </span>
                        <span :style="reasonOptionLabelStyle">{{ reason.name }}</span>
                    </label>
                </div>
                <div
                    :key="!saleLineItem.is_new_project && !saleLineItem.is_new_retainer
                        ? `${saleLineItem.deal_line_item.id}__${saleLineItem.period}__explanation`
                        : `${saleLineItem.id}__explanation`"
                    :style="explanationFieldStyle"
                >
                    <label :style="labelStyle">Explanation</label>
                    <textarea
                        v-model="saleLineItem.explanation"
                        :style="textareaStyle"
                        @input="onExplanationInput"
                    ></textarea>
                </div>
            </div>
        </CCard>

        <!-- ===== Q3: Did you receive payment? ===== -->
        <CCard
            v-if="questionnnare.is_provide_service && questionnnare.is_issue_invoice
                && (!isMobile || subStepIndex !== 3)"
            :extra-style="cardStyle"
        >
            <div :style="qHeadStyle">
                <span :style="qNumStyle">3</span>
                <span :style="qLabelStyle">Question 3 of 3</span>
            </div>
            <h2 :style="qTitleStyle">Did you receive payment?</h2>
            <div :style="choicePairStyle">
                <CChoice
                    icon="$"
                    label="Yes, paid"
                    sub="In full or in part"
                    tone="green"
                    :selected="questionnnare.is_receive_payment === true"
                    @click="onChangeReceivePayment(true)"
                />
                <CChoice
                    icon="–"
                    label="Not yet"
                    sub="Will carry to next month"
                    tone="neutral"
                    :selected="questionnnare.is_receive_payment === false"
                    @click="onChangeReceivePayment(false)"
                />
            </div>
        </CCard>

        <!-- ===== Mobile-only Q4 heading + commission hero =====
             On mobile we hoist the Q4 question label, title, lead and the
             commission card OUT of the form CCard so the dark hero sits
             at the top of the screen (matching the designer's Amounts
             mockup). Desktop renders this stuff inside the form CCard
             below — the v-if branches in qHeadStyle / commissionCardStyle
             keep desktop unchanged. -->
        <template
            v-if="isMobile && subStepIndex === 3
                && questionnnare.is_issue_invoice === true
                && questionnnare.is_provide_service === true
                && questionnnare.is_receive_payment !== null"
        >
            <div :style="q4MobileHeadStyle">
                <div :style="qLabelStyle">Question 4 of 4</div>
                <h2 :style="qTitleStyle" class="c-mqs__q4-title">
                    <span v-if="questionnnare.is_receive_payment">Now, the numbers.</span>
                    <span v-else>How much was invoiced?</span>
                </h2>
                <p v-if="questionnnare.is_receive_payment" :style="q4MobileLeadStyle">
                    Invoiced and collected sit side-by-side; hard cost below.
                    Commission updates live.
                </p>
            </div>

            <div v-if="questionnnare.is_receive_payment" :style="commissionHeroStyleMobile">
                <div :style="commissionHeroLabelStyle">
                    Your commission · {{ currentMonth }}
                </div>
                <div :style="commissionHeroValueStyle">
                    ${{ numberWithCommas(saleLineItem.commission_owed) }}
                </div>
                <div :style="commissionHeroBreakdownStyle">
                    ${{ numberWithCommas(saleLineItem.collected) }} collected
                    − ${{ numberWithCommas(saleLineItem.hard_cost) }} hard cost
                    × <strong>{{ saleLineItem.procent }}%</strong>
                </div>
            </div>
        </template>

        <!-- ===== Financial inputs + commission card ===== -->
        <CCard
            v-if="questionnnare.is_issue_invoice === true
                && questionnnare.is_provide_service === true
                && questionnnare.is_receive_payment !== null"
            :extra-style="cardStyle"
        >
            <!-- Desktop-only Q4 header (mobile renders this above the card) -->
            <template v-if="!isMobile || subStepIndex !== 3">
                <div :style="qHeadStyle">
                    <span :style="qNumStyle">4</span>
                    <span :style="qLabelStyle">Record the numbers</span>
                </div>
                <h2 :style="qTitleStyle">
                    <span v-if="questionnnare.is_receive_payment">Now, the numbers.</span>
                    <span v-else>How much was invoiced?</span>
                </h2>
            </template>

            <!-- Branch 1: existing (non-new) project/retainer -->
            <template v-if="!saleLineItem.is_new_project && !saleLineItem.is_new_retainer">
                <div :style="amountGridStyle">
                    <CField label="Invoice number" optional>
                        <div
                            v-if="saleLineItem.status.id === 1 || saleLineItem.status.id === 12 || saleLineItem.status.id === 14"
                            :style="inputWrapStyle"
                        >
                            <input
                                v-model="saleLineItem.invoice_number"
                                :style="bareInputStyle"
                                @input="(e) => updateInvoiceNumber(e.target.value, saleLineItem.sale.id, 'invoice_number', saleLineItem.id)"
                            />
                        </div>
                        <div v-else :style="readonlyValueStyle">{{ saleLineItem.invoice_number }}</div>
                    </CField>

                    <CField label="Amount">
                        <div
                            v-if="saleLineItem.status.id === 1 || saleLineItem.status.id === 12 || saleLineItem.status.id === 14 || saleLineItem.status.id === 15 || saleLineItem.status.id === 10"
                            :style="currencyWrapStyle"
                        >
                            <CurrencyInput
                                :options="currencyInputSetting"
                                :value="saleLineItem.amount"
                                :change="(value, _key) => { updateInputAmount(value, saleLineItem.id, _key); }"
                                :key_input="'amount'"
                                :isLoading="isLoading"
                            />
                        </div>
                        <div v-else :style="readonlyValueStyle">${{ numberWithCommas(saleLineItem.amount) }}</div>
                    </CField>

                    <CField label="Outstanding balance">
                        <div :style="readonlyValueStyle">${{ saleLineItem.outstanding_balance }}</div>
                    </CField>

                    <CField label="Required payment">
                        <div :style="readonlyValueStyle">${{ requiredPayment }}</div>
                    </CField>

                    <CField v-if="questionnnare.is_receive_payment" label="Collected">
                        <div
                            v-if="saleLineItem.status.id === 1 || saleLineItem.status.id === 5 || saleLineItem.status.id === 12 || saleLineItem.status.id === 14 || saleLineItem.status.id === 15 || saleLineItem.status.id === 10"
                            :style="currencyWrapStyle"
                        >
                            <CurrencyInput
                                :options="currencyInputSetting"
                                :value="saleLineItem.collected"
                                :change="(value, _key) => { updateInputCollected(value, saleLineItem.id, _key); }"
                                :key_input="'collected'"
                                :isLoading="isLoading"
                            />
                        </div>
                        <div v-else :style="readonlyValueStyle">${{ numberWithCommas(saleLineItem.collected) }}</div>
                    </CField>

                    <CField
                        v-if="saleLineItem.deal_line_item.deal.commission_type_id !== 1"
                        label="Hard cost"
                    >
                        <div
                            v-if="(saleLineItem.deal_line_item.deal.commission_type_id === 2 && saleLineItem.status.id === 1) || saleLineItem.status.id === 5 || saleLineItem.status.id === 12 || saleLineItem.status.id === 14"
                            :style="currencyWrapStyle"
                        >
                            <CurrencyInput
                                :options="currencyInputSetting"
                                :value="saleLineItem.hard_cost"
                                :disabled="!saleLineItem.collected || saleLineItem.collected <= 0"
                                :change="(value, _key) => { updateInputHardCost(value, saleLineItem.id, _key); }"
                                :key_input="'hard_cost'"
                                :isLoading="isLoading"
                            />
                        </div>
                        <div v-else :style="readonlyValueStyle">${{ numberWithCommas(saleLineItem.hard_cost) }}</div>
                    </CField>
                </div>

                <!-- Hard-cost cap explanation (existing project branch) -->
                <div
                    v-if="saleLineItem.deal_line_item.deal.commission_type_id !== 1
                        && Number(saleLineItem.hard_cost) > Number(saleLineItem.amount) * (Number(saleLineItem.deal_line_item.deal.cap) / 100)"
                    :style="explanationFieldStyle"
                >
                    <label :style="labelStyle">Explanation</label>
                    <textarea
                        v-model="saleLineItem.explanation"
                        :style="textareaStyle"
                        @input="onExplanationInput"
                    ></textarea>
                </div>
            </template>

            <!-- Branch 2: new project / new retainer -->
            <template v-else>
                <div :style="amountGridStyle">
                    <CField label="Invoice number" optional>
                        <div
                            v-if="saleLineItem.status.id === 1 || saleLineItem.status.id === 12 || saleLineItem.status.id === 14 || saleLineItem.status.id === 15 || saleLineItem.status.id === 10"
                            :style="inputWrapStyle"
                        >
                            <input
                                v-model="saleLineItem.invoice_number"
                                :style="bareInputStyle"
                                @input="(e) => updateProjectInvoiceNumber(e.target.value, saleLineItem.deal_line_item.id, 'invoice_number', saleLineItem.is_new_retainer, saleLineItem.period)"
                            />
                        </div>
                        <div v-else :style="readonlyValueStyle">{{ saleLineItem.invoice_number }}</div>
                    </CField>

                    <CField label="Amount">
                        <div :style="currencyWrapStyle">
                            <CurrencyInput
                                :options="currencyInputSetting"
                                :value="saleLineItem.amount"
                                :change="(value, _key) => { updateProjectInputAmount(value, saleLineItem.deal_line_item.id, _key, saleLineItem.is_new_retainer, saleLineItem.period); }"
                                :key_input="'amount'"
                                :isLoading="isLoading"
                            />
                        </div>
                    </CField>

                    <CField label="Outstanding balance">
                        <div :style="readonlyValueStyle">${{ saleLineItem.outstanding_balance }}</div>
                    </CField>

                    <CField label="Required payment">
                        <div :style="readonlyValueStyle">${{ requiredPayment }}</div>
                    </CField>

                    <CField v-if="questionnnare.is_receive_payment" label="Collected">
                        <div :style="currencyWrapStyle">
                            <CurrencyInput
                                :options="currencyInputSetting"
                                :value="saleLineItem.collected"
                                :change="(value, _key) => { updateProjectInputCollected(value, saleLineItem.deal_line_item.id, _key, saleLineItem.is_new_retainer, saleLineItem.period); }"
                                :key_input="'collected'"
                                :isLoading="isLoading"
                            />
                        </div>
                    </CField>

                    <CField
                        v-if="saleLineItem.deal_line_item.deal.commission_type_id !== 1"
                        label="Hard cost"
                    >
                        <div :style="currencyWrapStyle">
                            <CurrencyInput
                                :options="currencyInputSetting"
                                :value="saleLineItem.hard_cost"
                                :disabled="!saleLineItem.collected || saleLineItem.collected <= 0"
                                :change="(value, _key) => { updateProjectInputHardCost(value, saleLineItem.deal_line_item.id, _key, saleLineItem.is_new_retainer, saleLineItem.period); }"
                                :key_input="'hard_cost'"
                                :isLoading="isLoading"
                            />
                        </div>
                    </CField>
                </div>

                <!-- Hard-cost cap explanation (new project branch) -->
                <div
                    v-if="saleLineItem.deal_line_item.deal.commission_type_id !== 1
                        && Number(saleLineItem.hard_cost) > Number(saleLineItem.amount) * (Number(saleLineItem.deal_line_item.deal.cap) / 100)"
                    :style="explanationFieldStyle"
                >
                    <label :style="labelStyle">Explanation</label>
                    <textarea
                        v-model="saleLineItem.explanation"
                        :style="textareaStyle"
                        @input="onExplanationInput"
                    ></textarea>
                </div>
            </template>

            <!-- Eligible amount + commission stats. Desktop-only — on
                 mobile the hero rendered above the form already shows
                 these numbers, so we skip the bottom card to avoid
                 duplicate "Your commission" content. -->
            <div
                v-if="questionnnare.is_receive_payment && (!isMobile || subStepIndex !== 3)"
                :style="commissionCardStyle"
            >
                <div :style="commissionRowStyle">
                    <div>
                        <div :style="commissionLabelStyle">Your commission this period</div>
                        <div :style="commissionValueStyle">${{ saleLineItem.commission_owed }}</div>
                    </div>
                    <div :style="commissionBreakdownStyle">
                        <div>${{ saleLineItem.collected }} collected</div>
                        <div>− ${{ saleLineItem.hard_cost }} hard cost</div>
                        <div :style="commissionBreakdownLineStyle">
                            ${{ saleLineItem.eligible_amount }} × {{ saleLineItem.procent }}%
                        </div>
                    </div>
                </div>
            </div>
        </CCard>

        <!-- ===== Mobile-only SERVICE BRIEF card =====
             Mirrors what ServiceDetailsSidebar would show on desktop —
             since the sidebar is hidden under 768px (M9) we surface a
             compact summary at the bottom of the Q4 amounts screen. -->
        <CCard
            v-if="isMobile && subStepIndex === 3
                && questionnnare.is_issue_invoice === true
                && questionnnare.is_provide_service === true
                && questionnnare.is_receive_payment !== null"
            :extra-style="serviceBriefCardStyle"
        >
            <div :style="serviceBriefEyebrowStyle">SERVICE BRIEF</div>
            <div :style="serviceBriefGridStyle">
                <div>
                    <div :style="serviceBriefLabelStyle">Service</div>
                    <div :style="serviceBriefValueStyle">{{ briefServiceLabel }}</div>
                </div>
                <div>
                    <div :style="serviceBriefLabelStyle">Type</div>
                    <div :style="serviceBriefValueStyle">{{ briefTypeLabel }}</div>
                </div>
                <div v-if="briefKickOff">
                    <div :style="serviceBriefLabelStyle">Kick-off</div>
                    <div :style="serviceBriefValueStyle">{{ briefKickOff }}</div>
                </div>
                <div v-if="briefRate">
                    <div :style="serviceBriefLabelStyle">Rate</div>
                    <div :style="serviceBriefValueStyle">{{ briefRate }}</div>
                </div>
            </div>
        </CCard>

        <!-- ===== Collected-less-than-invoice reason ===== -->
        <CCard
            v-if="((!isCollectedSection && questionnnare.is_provide_service === true))
                && questionnnare.is_receive_payment === true
                && (((Number(saleLineItem.collected) + Number(saleLineItem.outstanding_balance)) < Number(saleLineItem.amount)
                    && (Number(saleLineItem.amount) - (Number(saleLineItem.collected) + Number(saleLineItem.outstanding_balance)))
                       > Number(saleLineItem.amount - saleLineItem.outstanding_balance) * threshold)
                    && (saleLineItem.collected || saleLineItem.collected == 0)
                    && saleLineItem.amount)"
            :extra-style="cardStyle"
        >
            <h2 :style="qTitleStyle">
                Please specify why the collected amount is less than the invoice amount
            </h2>
            <div :style="reasonGridStyle">
                <label
                    v-for="(reason, index) in collectedLessAmount"
                    :key="`collected_reason_${reason.id}`"
                    :for="`reason_${reason.id}`"
                    :style="reasonOptionStyle(selectedReason === reason.id)"
                >
                    <input
                        type="radio"
                        :id="`reason_${reason.id}`"
                        :checked="selectedReason === reason.id"
                        name="reason_id"
                        :value="+reason.id"
                        :style="srOnly"
                        @change="$emit('update:selected-reason', Number($event.target.value)); $emit('change-reason', $event)"
                    />
                    <span :style="reasonRadioDotStyle(selectedReason === reason.id)">
                        <span v-if="selectedReason === reason.id" :style="reasonRadioInnerStyle" />
                    </span>
                    <span :style="reasonOptionLabelStyle">{{ reason.name }}</span>
                </label>
            </div>
            <div :style="explanationFieldStyle">
                <label :style="labelStyle">Explanation</label>
                <textarea
                    v-model="saleLineItem.explanation"
                    :style="textareaStyle"
                    @input="onExplanationInput"
                ></textarea>
            </div>
        </CCard>

        <!-- ===== Collected-more-than-invoice reason ===== -->
        <CCard
            v-else-if="((!isCollectedSection && questionnnare.is_provide_service === true))
                && questionnnare.is_receive_payment === true
                && (((Number(saleLineItem.collected) + Number(saleLineItem.outstanding_balance)) > Number(saleLineItem.amount)
                    && ((Number(saleLineItem.collected) + Number(saleLineItem.outstanding_balance)) - Number(saleLineItem.amount))
                       > Number(saleLineItem.amount - saleLineItem.outstanding_balance) * threshold)
                    && saleLineItem.collected
                    && saleLineItem.amount)"
            :extra-style="cardStyle"
        >
            <h2 :style="qTitleStyle">
                Please specify why the collected amount is more than the invoice amount
            </h2>
            <div :style="reasonGridStyle">
                <label
                    v-for="(reason, index) in amountLessCollected"
                    :key="`collected_reason_${reason.id}`"
                    :for="`reason_${reason.id}`"
                    :style="reasonOptionStyle(selectedReason === reason.id)"
                >
                    <input
                        type="radio"
                        :id="`reason_${reason.id}`"
                        :checked="selectedReason === reason.id"
                        name="reason_id"
                        :value="+reason.id"
                        :style="srOnly"
                        @change="$emit('update:selected-reason', Number($event.target.value)); $emit('change-reason', $event)"
                    />
                    <span :style="reasonRadioDotStyle(selectedReason === reason.id)">
                        <span v-if="selectedReason === reason.id" :style="reasonRadioInnerStyle" />
                    </span>
                    <span :style="reasonOptionLabelStyle">{{ reason.name }}</span>
                </label>
            </div>
            <div :style="explanationFieldStyle">
                <label :style="labelStyle">Explanation</label>
                <textarea
                    v-model="saleLineItem.explanation"
                    :style="textareaStyle"
                    @input="onExplanationInput"
                ></textarea>
            </div>
        </CCard>
    </div>
</template>

<script>
import CurrencyInput from "./CurrencyInput";

import CCard from "../c-design/CCard.vue";
import CChoice from "../c-design/CChoice.vue";
import CField from "../c-design/CField.vue";
import SubStepTrail from "../c-design/SubStepTrail.vue";
import viewportMixin from "../c-design/useViewport";
import {
    C_INK,
    C_INK2,
    C_MUTED,
    C_HAIR,
    C_CREAM,
    C_RED,
    C_PANEL,
    TAG_RED_DEEP,
    FONT_DISPLAY,
    FONT_BODY,
} from "../c-design/tokens";

/**
 * Extracted from EditSaleLineItem.vue (lines 18–1000 pre-refactor — the
 * entry-state "Did you provide service / issue invoice / receive payment"
 * 3-question interview). Sibling of the 6 already-extracted state screens
 * (Overdue, ServiceDetailsSidebar, AmountDiffReason, NoServiceProvided,
 * BillingDelayed, CollectedInformation). Same prop+emit pattern as them.
 *
 * Guarded in the parent by:
 *   !is_no_service_prodided && !is_billing_delayed && !is_amount_diff
 *   && !is_collected_section && !is_not_value_for_collected
 *
 * saleLineItem is mutated in place via v-model on `.explanation`,
 * `.invoice_number`, `.amount` — object-by-reference, same as pre-refactor.
 * `selectedReason` uses `.sync`: emits `update:selected-reason` from the
 * reason radios; `change-reason` is the side-effect event the parent's
 * onChangeReasonType handler reads.
 *
 * Variant-C visual port (Stage 4 of wizard redesign): each question is
 * a CCard with progressive disclosure (Q2 reveals after Q1=true, Q3 after
 * Q1=true && Q2=true, financial form after Q3 has any answer). Yes/No
 * radios are replaced by CChoice big-button pairs; reason pickers use
 * styled <label> wrappers around hidden radios so the visual is a
 * choice-card grid while the form contract stays radio-driven.
 */
export default {
    name: "MainQuestionsScreen",
    mixins: [viewportMixin],
    components: { CurrencyInput, CCard, CChoice, CField, SubStepTrail },
    props: {
        saleLineItem: { type: Object, required: true },
        currentMonth: { type: String, default: "" },
        questionnnare: { type: Object, required: true },
        selectedReason: { default: null },
        reasonsList: { type: Array, default: () => [] },
        billingReasons: { type: Array, default: () => [] },
        collectedLessAmount: { type: Array, default: () => [] },
        amountLessCollected: { type: Array, default: () => [] },
        currencyInputSetting: { type: Object, default: () => ({}) },
        isLoading: { type: Boolean, default: false },
        isCollectedSection: { type: Boolean, default: false },
        threshold: { default: null },
        numberWithCommas: { type: Function, required: true },
        onChangeProvideService: { type: Function, required: true },
        onChangeIssueService: { type: Function, required: true },
        onChangeReceivePayment: { type: Function, required: true },
        updateInvoiceNumber: { type: Function, required: true },
        updateProjectInvoiceNumber: { type: Function, required: true },
        updateInputAmount: { type: Function, required: true },
        updateProjectInputAmount: { type: Function, required: true },
        updateInputCollected: { type: Function, required: true },
        updateProjectInputCollected: { type: Function, required: true },
        updateInputHardCost: { type: Function, required: true },
        updateProjectInputHardCost: { type: Function, required: true },
        updateExplanation: { type: Function, required: true },
        updateProjectExplanation: { type: Function, required: true },
    },
    computed: {
        // ===== SubStepTrail wiring (mobile pill trail) =====
        // Map the four wizard questions to a 0-3 trail index so the
        // SubStepTrail pill knows what to highlight as "current". The
        // user advances Service → Invoice → Payment → Amounts; we hold
        // at the first question that hasn't been answered yet. When a
        // question is answered "No", the trail stays on it (the user
        // is now filling out the reason picker, still on that step).
        subStepIndex() {
            const q = this.questionnnare || {};
            if (q.is_provide_service === null || q.is_provide_service === undefined) return 0;
            if (q.is_provide_service === false) return 0; // No-path on Service
            if (q.is_issue_invoice === null || q.is_issue_invoice === undefined) return 1;
            if (q.is_issue_invoice === false) return 1; // No-path on Invoice
            if (q.is_receive_payment === null || q.is_receive_payment === undefined) return 2;
            return 3; // amounts/financial form is open
        },
        // Past-step colouring: 'yes' → green check, 'no' → grey dash.
        // SubStepTrail only consults answers[id] for steps where
        // i < subStepIndex (i.e. already done), so values for the
        // current/future steps don't matter here.
        subStepAnswers() {
            const q = this.questionnnare || {};
            return {
                service: q.is_provide_service === false ? 'no' : 'yes',
                invoice: q.is_issue_invoice === false ? 'no' : 'yes',
                payment: q.is_receive_payment === false ? 'no' : 'yes',
            };
        },
        // ===== Mobile-only SERVICE BRIEF data =====
        // ServiceDetailsSidebar is hidden under 768px (M9), so we surface
        // a compact summary at the bottom of the Q4 amounts screen. Each
        // field has multiple fallbacks because the live data shape isn't
        // perfectly consistent across new-project / new-retainer / existing
        // branches; the safest existing-sidebar accessors are mirrored.
        briefServiceLabel() {
            const sli = this.saleLineItem || {};
            if (sli.service && sli.service.label) return sli.service.label;
            if (sli.deal_line_item && sli.deal_line_item.service && sli.deal_line_item.service.label) {
                return sli.deal_line_item.service.label;
            }
            if (sli.deal_line_item && sli.deal_line_item.name) return sli.deal_line_item.name;
            return '—';
        },
        briefTypeLabel() {
            const id = this.saleLineItem && this.saleLineItem.deal_line_item
                ? this.saleLineItem.deal_line_item.type_id
                : null;
            if (id === 1) return 'Retainer';
            if (id === 2) return 'Project';
            if (id === 3) return 'One-Time Fee';
            return 'Deal';
        },
        briefKickOff() {
            const sli = this.saleLineItem || {};
            const raw = (sli.deal_line_item && sli.deal_line_item.kick_off_date)
                || sli.kick_off_date
                || null;
            if (!raw) return '';
            // The kick-off field comes through as YYYY-MM-DD; format to
            // "MMM YYYY" without pulling moment into this file. JS Date
            // parsing of YYYY-MM-DD is reliable.
            const d = new Date(raw);
            if (Number.isNaN(d.getTime())) return raw;
            const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            return months[d.getMonth()] + ' ' + d.getFullYear();
        },
        briefRate() {
            const sli = this.saleLineItem || {};
            if (sli.procent || sli.procent === 0) return sli.procent + '%';
            return '';
        },
        // Safe difference of amount − outstanding_balance. The backend
        // sometimes passes these as comma-separated strings ("1,157.35"),
        // and `1575 - "1,157.35"` evaluates to NaN because JS's numeric
        // coercion can't parse the comma. parseLoose strips commas /
        // dollar signs / whitespace so both branches are real numbers
        // before subtraction. Pre-existing bug surfaced by Stage 4's
        // cleaner CField rendering (the legacy table hid the NaN
        // behind a label-value grid that read less obviously).
        requiredPayment() {
            const parseLoose = (v) => {
                if (v === null || v === undefined || v === '') return 0;
                const n = parseFloat(String(v).replace(/[,$\s]/g, ''));
                return Number.isFinite(n) ? n : 0;
            };
            const diff = parseLoose(this.saleLineItem.amount)
                - parseLoose(this.saleLineItem.outstanding_balance);
            return this.numberWithCommas(diff);
        },
        // ===== c-design style maps =====
        cardStyle() {
            return {
                marginBottom: this.isMobile ? '14px' : '18px',
                padding: this.isMobile ? '22px' : '32px 36px',
            };
        },
        qHeadStyle() {
            return {
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '6px',
            };
        },
        qNumStyle() {
            return {
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: C_INK,
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: FONT_DISPLAY,
                fontWeight: 700,
                fontSize: '12px',
                flexShrink: 0,
            };
        },
        qLabelStyle() {
            return {
                fontSize: '12px',
                color: C_MUTED,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                fontWeight: 600,
            };
        },
        qTitleStyle() {
            return {
                margin: this.isMobile ? '0 0 18px' : '0 0 22px',
                fontFamily: FONT_DISPLAY,
                fontSize: this.isMobile ? '24px' : '28px',
                fontWeight: 700,
                letterSpacing: this.isMobile ? '-0.6px' : '-0.7px',
                lineHeight: 1.15,
                color: C_INK,
            };
        },
        accentStyle() {
            return { color: C_RED };
        },
        choicePairStyle() {
            return {
                display: 'grid',
                gridTemplateColumns: this.isMobile ? '1fr' : '1fr 1fr',
                gap: this.isMobile ? '10px' : '12px',
            };
        },
        reasonBlockStyle() {
            return {
                marginTop: '24px',
                paddingTop: '20px',
                borderTop: `1px solid ${C_HAIR}`,
            };
        },
        reasonTitleStyle() {
            return {
                margin: '0 0 14px',
                fontSize: '14px',
                fontWeight: 600,
                color: C_INK2,
                lineHeight: 1.5,
            };
        },
        reasonGridStyle() {
            return {
                display: 'grid',
                gridTemplateColumns: this.isMobile ? '1fr' : '1fr 1fr',
                gap: this.isMobile ? '8px' : '10px',
            };
        },
        reasonRadioInnerStyle() {
            return {
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#fff',
                display: 'inline-block',
            };
        },
        reasonOptionLabelStyle() {
            return {
                fontSize: '14px',
                fontWeight: 600,
                color: 'inherit',
            };
        },
        srOnly() {
            return {
                position: 'absolute',
                width: '1px',
                height: '1px',
                margin: '-1px',
                overflow: 'hidden',
                clip: 'rect(0 0 0 0)',
                whiteSpace: 'nowrap',
                border: '0',
            };
        },
        explanationFieldStyle() {
            return { marginTop: '18px' };
        },
        labelStyle() {
            return {
                display: 'block',
                fontSize: '12px',
                color: C_INK2,
                fontWeight: 600,
                marginBottom: '6px',
            };
        },
        textareaStyle() {
            return {
                width: '100%',
                minHeight: '90px',
                padding: '14px',
                border: `1.5px solid ${C_HAIR}`,
                borderRadius: '14px',
                background: C_CREAM,
                resize: 'vertical',
                fontFamily: FONT_BODY,
                fontSize: '14px',
                color: C_INK,
                outline: 'none',
            };
        },
        amountGridStyle() {
            // Q4 financial form has long labels like "Outstanding balance"
            // and "Required payment" — they don't fit two-per-row on phone
            // viewports (~390px). Collapse to 1-col on mobile. Desktop
            // keeps the 2-col grid.
            return {
                display: 'grid',
                gridTemplateColumns: this.isMobile ? '1fr' : '1fr 1fr',
                gap: this.isMobile ? '10px' : '14px',
            };
        },
        inputWrapStyle() {
            return {
                display: 'flex',
                alignItems: 'center',
                border: `1.5px solid ${C_HAIR}`,
                borderRadius: '14px',
                background: C_CREAM,
                padding: '0 16px',
            };
        },
        bareInputStyle() {
            return {
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                padding: '14px 0',
                fontSize: '16px',
                fontFamily: FONT_BODY,
                fontWeight: 500,
                color: C_INK,
                width: '100%',
            };
        },
        currencyWrapStyle() {
            return {
                display: 'flex',
                alignItems: 'center',
                border: `1.5px solid ${C_HAIR}`,
                borderRadius: '14px',
                background: C_CREAM,
                padding: '4px 16px',
            };
        },
        readonlyValueStyle() {
            return {
                padding: '14px 16px',
                fontFamily: FONT_DISPLAY,
                fontSize: '16px',
                fontWeight: 600,
                color: C_INK,
                background: '#fff',
                border: `1px solid ${C_HAIR}`,
                borderRadius: '14px',
            };
        },
        commissionCardStyle() {
            return {
                marginTop: this.isMobile ? '18px' : '24px',
                padding: this.isMobile ? '20px' : '22px 26px',
                background: C_INK,
                color: '#fff',
                borderRadius: this.isMobile ? '22px' : '16px',
            };
        },
        commissionRowStyle() {
            // Mobile stacks value + breakdown vertically per MobileQ4_Amounts.
            return {
                display: 'flex',
                flexDirection: this.isMobile ? 'column' : 'row',
                alignItems: this.isMobile ? 'flex-start' : 'center',
                justifyContent: 'space-between',
                gap: this.isMobile ? '8px' : '18px',
            };
        },
        commissionLabelStyle() {
            return {
                fontSize: '11px',
                color: '#FFFFFF99',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                fontWeight: 600,
            };
        },
        commissionValueStyle() {
            return {
                fontFamily: FONT_DISPLAY,
                fontSize: this.isMobile ? '40px' : '44px',
                fontWeight: 700,
                letterSpacing: this.isMobile ? '-1.4px' : '-1.5px',
                lineHeight: 1,
                marginTop: '6px',
            };
        },
        commissionBreakdownStyle() {
            return {
                textAlign: this.isMobile ? 'left' : 'right',
                fontSize: this.isMobile ? '11.5px' : '12px',
                color: '#FFFFFFB0',
                lineHeight: 1.6,
            };
        },
        commissionBreakdownLineStyle() {
            return {
                borderTop: '1px solid #FFFFFF33',
                marginTop: '4px',
                paddingTop: '4px',
            };
        },
        // ===== Mobile-only Q4 head + commission hero + service brief =====
        q4MobileHeadStyle() {
            return {
                padding: '6px 4px 0',
                marginBottom: '14px',
            };
        },
        q4MobileLeadStyle() {
            return {
                margin: '8px 0 0',
                fontSize: '13.5px',
                color: C_MUTED,
                lineHeight: 1.55,
            };
        },
        commissionHeroStyleMobile() {
            return {
                marginBottom: '14px',
                padding: '22px 24px',
                background: C_INK,
                color: '#fff',
                borderRadius: '22px',
            };
        },
        commissionHeroLabelStyle() {
            return {
                fontSize: '11px',
                color: '#FFFFFFB0',
                letterSpacing: '0.7px',
                textTransform: 'uppercase',
                fontWeight: 700,
            };
        },
        commissionHeroValueStyle() {
            return {
                fontFamily: FONT_DISPLAY,
                fontSize: '48px',
                fontWeight: 700,
                letterSpacing: '-1.6px',
                lineHeight: 1,
                marginTop: '10px',
            };
        },
        commissionHeroBreakdownStyle() {
            return {
                fontSize: '12.5px',
                color: '#FFFFFFB0',
                marginTop: '10px',
                lineHeight: 1.5,
            };
        },
        serviceBriefCardStyle() {
            return {
                marginTop: '6px',
                marginBottom: '14px',
                padding: '20px 22px',
            };
        },
        serviceBriefEyebrowStyle() {
            return {
                fontSize: '11px',
                color: C_MUTED,
                letterSpacing: '0.7px',
                textTransform: 'uppercase',
                fontWeight: 700,
                marginBottom: '14px',
            };
        },
        serviceBriefGridStyle() {
            return {
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '14px 18px',
            };
        },
        serviceBriefLabelStyle() {
            return {
                fontSize: '10.5px',
                color: C_MUTED,
                letterSpacing: '0.6px',
                textTransform: 'uppercase',
                fontWeight: 700,
                marginBottom: '4px',
            };
        },
        serviceBriefValueStyle() {
            return {
                fontFamily: FONT_DISPLAY,
                fontSize: '15px',
                fontWeight: 700,
                color: C_INK,
                letterSpacing: '-0.2px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            };
        },
    },
    methods: {
        reasonOptionStyle(selected) {
            return {
                textAlign: 'left',
                padding: '14px 16px',
                background: selected ? C_INK : '#fff',
                color: selected ? '#fff' : C_INK,
                border: `1.5px solid ${selected ? C_INK : C_HAIR}`,
                borderRadius: '12px',
                fontFamily: FONT_BODY,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                position: 'relative',
            };
        },
        reasonRadioDotStyle(selected) {
            return {
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                border: `1.5px solid ${selected ? '#fff' : C_HAIR}`,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            };
        },
        onExplanationInput(e) {
            if (!this.saleLineItem.is_new_project && !this.saleLineItem.is_new_retainer) {
                this.updateExplanation(e.target.value, this.saleLineItem.id, 'explanation', null);
            } else {
                this.updateProjectExplanation(
                    e.target.value,
                    this.saleLineItem.deal_line_item.id,
                    'explanation',
                    this.saleLineItem.is_new_retainer,
                    this.saleLineItem.period
                );
            }
        },
    },
};
</script>

<style scoped>
/* Lives inside parent's Bootstrap .wizard_sale_wrap.row alongside
 * the (still-legacy-styled) ServiceDetailsSidebar; the col-xl-8 etc.
 * classes on the root <div> handle the flex-basis. */
.c-mqs {
    padding: 8px 16px 32px;
}
.c-mqs .red {
    color: #E63946;
}
</style>

<style>
/* CurrencyInput (vue-currency-input lib) renders a bare <input> with
 * browser-default width — inside our flex currencyWrapStyle wrappers
 * it refuses to fill the parent and the surrounding card overflows
 * the viewport on iPhone. Force the input to fill its flex parent.
 * Scoped to .c-mqs so the legacy `.wizard .form-field input.currency
 * -input { width: 95px }` rule still wins for non-c-design code. */
.c-mqs .currency-input {
    width: 100% !important;
    min-width: 0;
    box-sizing: border-box;
    border: none;
    outline: none;
    background: transparent;
    padding: 14px 0;
    font-size: 16px;
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    color: #1F1B17;
}
</style>
